import GATSBY_COMPILED_MDX from "/opt/build/repo/src/Popover/README.md";
import React, {useContext, useEffect, useState} from 'react';
import {Link} from 'gatsby';
import {MDXProvider} from '@mdx-js/react';
import classNames from 'classnames';
import {Container, Alert, breakpoints, useMediaQuery, Stack} from '~paragon-react';
import {SettingsContext} from '../context/SettingsContext';
import CodeBlock from '../components/CodeBlock';
import GenericPropsTable from '../components/PropsTable';
import Layout from '../components/PageLayout';
import SEO from '../components/SEO';
import LinkedHeading from '../components/LinkedHeading';
import ComponentsUsage from '../components/insights/ComponentsUsage';
import LeaveFeedback from '../components/LeaveFeedback';
import PageEditBtn from '../components/PageEditBtn';
import ComponentVariablesTable from '../components/ComponentVariablesTable';
const customMdxComponents = {
  h2: props => React.createElement(LinkedHeading, Object.assign({
    h: "2"
  }, props)),
  h3: props => React.createElement(LinkedHeading, Object.assign({
    h: "3"
  }, props)),
  h4: props => React.createElement(LinkedHeading, Object.assign({
    h: "4"
  }, props)),
  h5: props => React.createElement(LinkedHeading, Object.assign({
    h: "5"
  }, props)),
  h6: props => React.createElement(LinkedHeading, Object.assign({
    h: "6"
  }, props)),
  pre: props => React.createElement("div", props),
  code: CodeBlock,
  Link
};
function PageTemplate({data: {mdx, components: componentNodes}, pageContext: {cssVariablesData, componentsUsageInsights, githubEditPath}, children}) {
  var _mdx$frontmatter, _mdx$frontmatter2, _mdx$frontmatter2$sta, _components$sortedCom;
  const isMobile = useMediaQuery({
    maxWidth: breakpoints.large.maxWidth
  });
  const {0: showMinimizedTitle, 1: setShowMinimizedTitle} = useState(false);
  const {settings} = useContext(SettingsContext);
  const components = componentNodes.nodes.reduce((acc, currentValue) => {
    acc[currentValue.displayName] = currentValue;
    return acc;
  }, {});
  const cssVariablesTitle = 'Theme Variables';
  const cssVariablesUrl = 'theme-variables';
  const propsAPITitle = 'Props API';
  const propsAPIUrl = 'props-api';
  const usageInsightsTitle = 'Usage Insights';
  const usageInsightsUrl = 'usage-insights';
  const sortedComponentNames = ((_mdx$frontmatter = mdx.frontmatter) === null || _mdx$frontmatter === void 0 ? void 0 : _mdx$frontmatter.components) || [];
  const filteredComponentsUsageInsights = componentsUsageInsights.map(componentName => componentName.replace(/\./g, ''));
  const isUsageInsights = sortedComponentNames.some(value => filteredComponentsUsageInsights.includes(value));
  const getTocData = () => {
    var _tableOfContents$item, _tableOfContents$item3;
    const tableOfContents = JSON.parse(JSON.stringify(mdx.tableOfContents));
    if (cssVariablesData !== null && cssVariablesData !== void 0 && cssVariablesData.length && !((_tableOfContents$item = tableOfContents.items) !== null && _tableOfContents$item !== void 0 && _tableOfContents$item.includes())) {
      var _tableOfContents$item2;
      (_tableOfContents$item2 = tableOfContents.items) === null || _tableOfContents$item2 === void 0 ? void 0 : _tableOfContents$item2.push({
        title: cssVariablesTitle,
        url: `#${cssVariablesUrl}`
      });
    }
    (_tableOfContents$item3 = tableOfContents.items) === null || _tableOfContents$item3 === void 0 ? void 0 : _tableOfContents$item3.push({
      title: propsAPITitle,
      url: `#${propsAPIUrl}`
    });
    if (isUsageInsights) {
      var _tableOfContents$item4;
      (_tableOfContents$item4 = tableOfContents.items) === null || _tableOfContents$item4 === void 0 ? void 0 : _tableOfContents$item4.push({
        title: usageInsightsTitle,
        url: `#${usageInsightsUrl}`
      });
    }
    return tableOfContents;
  };
  const isDeprecated = ((_mdx$frontmatter2 = mdx.frontmatter) === null || _mdx$frontmatter2 === void 0 ? void 0 : (_mdx$frontmatter2$sta = _mdx$frontmatter2.status) === null || _mdx$frontmatter2$sta === void 0 ? void 0 : _mdx$frontmatter2$sta.toLowerCase().includes('deprecate')) || false;
  useEffect(() => setShowMinimizedTitle(!!isMobile), [isMobile]);
  return React.createElement(Layout, {
    showMinimizedTitle: showMinimizedTitle,
    isMdx: true,
    tocData: getTocData(),
    githubEditPath: githubEditPath
  }, React.createElement(SEO, {
    title: mdx.frontmatter.title
  }), React.createElement(Container, {
    size: settings.containerWidth,
    className: "py-5"
  }, isDeprecated && React.createElement(Alert, {
    variant: "warning"
  }, React.createElement(Alert.Heading, null, "This component will be removed soon."), React.createElement("p", {
    className: "small mb-0"
  }, mdx.frontmatter.notes)), React.createElement(Stack, {
    className: classNames('justify-content-between', {
      'flex-column-reverse align-items-start': isMobile
    }),
    direction: "horizontal"
  }, React.createElement("h1", null, mdx.frontmatter.title), React.createElement(Stack, {
    className: classNames('mb-2', {
      'justify-content-end': isMobile
    }),
    direction: "horizontal",
    gap: 2
  }, React.createElement(PageEditBtn, {
    githubEditPath: githubEditPath
  }), React.createElement(LeaveFeedback, null))), React.createElement(MDXProvider, {
    components: customMdxComponents
  }, children), !!(cssVariablesData !== null && cssVariablesData !== void 0 && cssVariablesData.length) && React.createElement("div", {
    className: "mb-5"
  }, React.createElement("h2", {
    className: "mb-4 pgn-doc__heading",
    id: cssVariablesUrl
  }, cssVariablesTitle, React.createElement("a", {
    href: `#${cssVariablesUrl}`,
    "aria-label": "Jump to CSS variables"
  }, React.createElement("span", {
    className: "pgn-doc__anchor"
  }, "#"))), React.createElement(ComponentVariablesTable, {
    rawStylesheet: cssVariablesData
  })), ((_components$sortedCom = components[sortedComponentNames[0]]) === null || _components$sortedCom === void 0 ? void 0 : _components$sortedCom.props) && React.createElement("h2", {
    className: "mb-5 pgn-doc__heading",
    id: propsAPIUrl
  }, propsAPITitle, React.createElement("a", {
    href: `#${propsAPIUrl}`,
    "aria-label": "Props API"
  }, React.createElement("span", {
    className: "pgn-doc__anchor"
  }, "#"))), typeof sortedComponentNames !== 'string' && (sortedComponentNames === null || sortedComponentNames === void 0 ? void 0 : sortedComponentNames.map(componentName => {
    const node = components[componentName];
    if (!node) {
      return null;
    }
    return React.createElement(GenericPropsTable, Object.assign({
      key: node.displayName
    }, node));
  })), isUsageInsights && React.createElement(React.Fragment, null, React.createElement("h2", {
    className: "pgn-doc__heading m-0",
    id: usageInsightsUrl
  }, usageInsightsTitle, React.createElement("a", {
    href: `#${usageInsightsUrl}`,
    "aria-label": "Usage Insights"
  }, React.createElement("span", {
    className: "pgn-doc__anchor"
  }, "#"))), React.createElement(ComponentsUsage, {
    data: sortedComponentNames
  }))));
}
export default function GatsbyMDXWrapper(props) {
  return React.createElement(PageTemplate, props, React.createElement(GATSBY_COMPILED_MDX, props));
}
const pageQuery = "252564335";
